import {
  Box,
  DialogActions,
  DialogContent,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import React from 'react';
import { Button } from '@ui/components';
import { openFileWithDataString } from '../../libs/openFile';
import { Dialog, DialogTitle } from '../common/Dialog';
import { Divider } from '../common/Divider';
import { CloseIconButton } from '../common/IconButton';
import { Typography } from '../common/Typography';
import { PaymentProvider } from '../services/AuthServiceTypes';
import { BillingTableItem, InvoiceCsvExportType } from './types';
import { useInvoiceCsvExporter } from './useInvoiceCsvExporter';
import { useInvoiceExport } from './useInvoiceExport';

export interface InvoiceExportModalProps {
  exportType: InvoiceCsvExportType;
  setExportType: (exportType: InvoiceCsvExportType) => void;
  onExport: () => void;
  onClose: () => void;
}

export const InvoiceExportModal: React.FC<InvoiceExportModalProps> = ({
  exportType,
  setExportType,
  onExport,
  onClose,
}) => (
  <>
    <DialogTitle>Export Invoice Data</DialogTitle>
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onExport();
      }}
    >
      <RadioGroup
        aria-label='export type'
        name='exportType'
        value={exportType}
        onChange={(event) => setExportType(event.target.value as InvoiceCsvExportType)}
      >
        <DialogContent>
          <FormControlLabel
            value='all'
            control={<Radio color='primary' />}
            label='Export all invoices'
          />
          <Box style={{ marginTop: '12px', marginBottom: '12px' }}>
            <Divider />
          </Box>
          <FormControlLabel
            value='paid'
            control={<Radio color='primary' />}
            label='Export paid invoices'
          />
          <FormControlLabel
            value='unpaid'
            control={<Radio color='primary' />}
            label='Export outstanding invoices'
          />
        </DialogContent>
      </RadioGroup>
      <Divider />
      <DialogActions>
        <Button type='submit'>
          <Typography variant='h5'>Export</Typography>
        </Button>
        <CloseIconButton onClick={onClose} />
      </DialogActions>
    </form>
  </>
);

interface InvoiceExportProps {
  invoices: BillingTableItem[];
  account: PaymentProvider;
}

const useStyles = makeStyles({
  root: { whiteSpace: 'nowrap', margin: 0 },
});

export const InvoiceExport: React.FC<InvoiceExportProps> = ({ account, ...props }) => {
  const classes = useStyles();
  const csvExporter = useInvoiceCsvExporter({ currency: account.currency });
  const { open, onClose, onOpen, ...otherProps } = useInvoiceExport({
    accountNumber: account.accountNumber,
    csvExporter,
    openFile: openFileWithDataString,
    ...props,
  });
  return (
    <>
      <Button variant='outlined' onClick={onOpen} classes={classes}>
        Export to .CSV
      </Button>
      <Dialog open={open} onClose={onClose} maxWidth='xs'>
        <InvoiceExportModal {...otherProps} onClose={onClose} />
      </Dialog>
    </>
  );
};
